import type { CommentId, UserId, ChecklistItemId, MessageId, Message, MockCache } from '../../types';
import { watchersOf } from '../../helpers';

import type { FanoutContext } from '.';
import type { InboxWish } from '../inbox-delivery';
import { channelLabel, mentionedUserIds } from '../internal';

export function commentSetAssigneeWishes({ cache, event, after, wishes }: FanoutContext): void {
  const commentId = event.entityId as CommentId;
  const comment = cache.comments.get(commentId);
  const assigneeId = after['assigneeId'];
  if (comment === undefined || typeof assigneeId !== 'string') {
    return;
  }
  const task = cache.tasks.get(comment.taskId);
  wishes.push({
    userId: assigneeId as unknown as UserId,
    kind: 'assigned_comment',
    sourceKind: 'comment',
    sourceId: String(commentId),
    taskId: comment.taskId,
    channelId: null,
    preview: `Вам назначен комментарий в ${task?.key ?? 'задаче'}: ${comment.bodyText.slice(0, 120)}`,
  });
}

export function checklistSetAssigneeWishes({ cache, event, after, wishes }: FanoutContext): void {
  const itemId = event.entityId as ChecklistItemId;
  const item = cache.checklist.get(itemId);
  const assigneeId = after['assigneeId'];
  if (item === undefined || typeof assigneeId !== 'string') {
    return;
  }
  const task = cache.tasks.get(item.taskId);
  wishes.push({
    userId: assigneeId as unknown as UserId,
    kind: 'assigned_check',
    sourceKind: 'task',
    sourceId: String(item.taskId),
    taskId: item.taskId,
    channelId: null,
    preview: `Вам назначен пункт чеклиста в ${task?.key ?? 'задаче'}: «${item.text}»`,
  });
}

export function commentAddWishes({ cache, event, wishes }: FanoutContext): void {
  const commentId = event.entityId as CommentId;
  const comment = cache.comments.get(commentId);
  if (comment === undefined) {
    return;
  }
  const task = cache.tasks.get(comment.taskId);
  const preview = `Новый комментарий в ${task?.key ?? 'задаче'}: ${comment.bodyText.slice(0, 120)}`;
  for (const watcher of watchersOf(cache, { kind: 'task', id: comment.taskId })) {
    if (!watcher.muted) {
      wishes.push({
        userId: watcher.userId,
        kind: 'comment',
        sourceKind: 'comment',
        sourceId: String(commentId),
        taskId: comment.taskId,
        channelId: null,
        preview,
      });
    }
  }
  for (const userId of mentionedUserIds(cache, 'comment', String(commentId))) {
    wishes.push({
      userId,
      kind: 'mention',
      sourceKind: 'comment',
      sourceId: String(commentId),
      taskId: comment.taskId,
      channelId: null,
      preview: `Вас упомянули в ${task?.key ?? 'задаче'}: ${comment.bodyText.slice(0, 120)}`,
    });
  }
  if (comment.replyToId !== null) {
    const parent = cache.comments.get(comment.replyToId);
    if (parent !== undefined && parent.deletedAt === null) {
      wishes.push({
        userId: parent.authorId,
        kind: 'comment_reply',
        sourceKind: 'comment',
        sourceId: String(commentId),
        taskId: comment.taskId,
        channelId: null,
        preview: `Ответ на ваш комментарий в ${task?.key ?? 'задаче'}: ${comment.bodyText.slice(0, 120)}`,
      });
    }
  }
}

export function messageWishes({ cache, event, wishes }: FanoutContext): void {
  const message = cache.messages.get(event.entityId as MessageId);
  if (message === undefined) {
    return;
  }
  const channel = cache.channels.get(message.channelId);
  const label = channel !== undefined ? channelLabel(cache, channel) : 'канале';
  for (const userId of mentionedUserIds(cache, 'message', String(message.id))) {
    wishes.push({
      userId,
      kind: 'chat_mention',
      sourceKind: 'message',
      sourceId: String(message.id),
      taskId: null,
      channelId: message.channelId,
      preview: `Вас упомянули в ${label}: ${message.bodyText.slice(0, 120)}`,
    });
  }
  wishes.push(...agentDirectWishes(cache, message));
  wishes.push(...agentThreadWishes(cache, message, label));
}

function isActiveAgent(cache: MockCache, userId: UserId): boolean {
  const user = cache.users.get(userId);
  return user !== undefined && user.kind === 'agent' && user.active;
}

function agentDirectWishes(cache: MockCache, message: Message): InboxWish[] {
  const channel = cache.channels.get(message.channelId);
  if (channel === undefined || channel.kind !== 'dm') {
    return [];
  }
  const authorName = cache.users.get(message.authorId)?.displayName ?? 'автора';
  const wishes: InboxWish[] = [];
  for (const member of cache.members.values()) {
    if (member.channelId !== message.channelId || member.userId === message.authorId || !isActiveAgent(cache, member.userId)) {
      continue;
    }
    wishes.push({
      userId: member.userId,
      kind: 'chat_dm',
      sourceKind: 'message',
      sourceId: String(message.id),
      taskId: null,
      channelId: message.channelId,
      preview: `Личное сообщение от ${authorName}: ${message.bodyText.slice(0, 120)}`,
      channelMuted: member.muted,
    });
  }
  return wishes;
}

function agentThreadWishes(cache: MockCache, message: Message, label: string): InboxWish[] {
  if (message.threadRootId === null) {
    return [];
  }
  const thread = cache.threads.get(message.threadRootId);
  if (thread === undefined) {
    return [];
  }
  const wishes: InboxWish[] = [];
  for (const userId of thread.followerIds) {
    if (userId === message.authorId || !isActiveAgent(cache, userId)) {
      continue;
    }
    wishes.push({
      userId,
      kind: 'thread_reply',
      sourceKind: 'message',
      sourceId: String(message.id),
      taskId: null,
      channelId: message.channelId,
      preview: `Ответ в треде в ${label}: ${message.bodyText.slice(0, 120)}`,
    });
  }
  return wishes;
}
